import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as React from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Field } from '../components/ui/Field';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { DataTable, type Column } from '../components/ui/DataTable';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import {
  AuditEventList,
  type AuditEventRow,
} from '../components/audit/AuditEventList';
import {
  DRIVER_STATUS_VALUES,
  type DriverStatusValue,
} from './_authenticated.drivers.index';

// Same UUID-shape regex as the route detail page. parseParams gates
// on this before mount, so /drivers/not-a-uuid renders the same UI
// as /drivers/<unknown-uuid>.
export const DRIVER_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isDriverIdParam(value: unknown): value is string {
  return typeof value === 'string' && DRIVER_ID_UUID_RE.test(value);
}

export const Route = createFileRoute('/_authenticated/drivers/$driverId')({
  component: DriverDetailPage,
  notFoundComponent: DriverNotFoundRoute,
  parseParams: (params): { driverId: string } => {
    if (!isDriverIdParam(params.driverId)) {
      throw notFound();
    }
    return { driverId: params.driverId };
  },
});

// ---------------------------------------------------------------------------
// Types — shapes returned from the loader queries
// ---------------------------------------------------------------------------

type DriverDetailRow = {
  id: string;
  full_name: string | null;
  email: string;
  employee_id: string | null;
  user_type: string;
  tenant_role: string | null;
  tenant_id: string | null;
  created_at: string;
  drivers: {
    status: string;
    phone: string | null;
    license_number: string | null;
    license_state: string | null;
    license_expires_on: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_color: string | null;
    vehicle_plate: string | null;
    vehicle_state: string | null;
    hire_date: string | null;
    notes: string | null;
  } | null;
};

type PharmacyAccessRow = {
  id: string;
  pharmacy_id: string;
  granted_at: string;
  pharmacies: {
    id: string;
    name: string;
    city: string | null;
  } | null;
  granter: { full_name: string | null; email: string } | null;
};

type RouteHistoryRow = {
  id: string;
  planned_for: string;
  route_status: string;
  stops: { count: number }[];
};

type PharmacyOption = {
  id: string;
  name: string;
  city: string | null;
};

type DetailState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      driver: DriverDetailRow;
      pharmacyAccess: PharmacyAccessRow[];
    }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function relativeTenure(hireIso: string | null, now: Date = new Date()): string {
  if (!hireIso) return '';
  const parts = hireIso.split('-');
  if (parts.length !== 3) return '';
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return '';
  const then = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (today.getTime() - then.getTime()) / (24 * 60 * 60 * 1000),
  );
  if (diffDays < 0) return '';
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) {
    const months = Math.round(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.round(diffDays / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function statusLabel(value: string): string {
  const words = value.split('_');
  const first = words[0];
  if (!first) return value;
  const head = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return [head, ...words.slice(1).map((w) => w.toLowerCase())].join(' ');
}

// LYNRX_* error mapper for every write path on this page. Single
// function so the prefix → copy table lives in one place; callers
// pass the area for ambient context.
function friendlyDriverError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_DRIVER_NOT_FOUND'))
    return 'Driver no longer accessible. Refresh.';
  if (m.includes('LYNRX_DRIVER_STATUS_UNCHANGED'))
    return 'Driver already has that status. Refresh.';
  if (m.includes('LYNRX_DRIVER_NO_CHANGES'))
    return 'No changes to save.';
  if (m.includes('LYNRX_INVALID_FIELD'))
    return 'One of the fields cannot be edited here.';
  if (m.includes('LYNRX_INVALID_DATE'))
    return 'Enter dates as YYYY-MM-DD.';
  if (m.includes('LYNRX_PHARMACY_ACCESS_EXISTS'))
    return 'Driver already has access to that pharmacy.';
  if (m.includes('LYNRX_PHARMACY_NOT_IN_TENANT'))
    return "That pharmacy isn't in this driver's tenant.";
  if (m.includes('LYNRX_PHARMACY_NOT_ACCESSIBLE'))
    return 'You do not have access to that pharmacy.';
  if (m.includes('LYNRX_PHARMACY_ACCESS_NOT_FOUND'))
    return 'That access grant is no longer accessible. Refresh.';
  if (m.includes('LYNRX_PHARMACY_NOT_FOUND'))
    return 'Pharmacy no longer accessible. Refresh.';
  return 'Action failed. Try again.';
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function DriverDetailPage(): JSX.Element {
  const { driverId } = Route.useParams();
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/drivers', search: { status: 'active' } });
  }, [navigate]);
  return <DriverDetailView driverId={driverId} onBack={onBack} />;
}

type ViewProps = {
  driverId: string;
  onBack: () => void;
  client?: LynrxSupabaseClient;
};

export function DriverDetailView({
  driverId,
  onBack,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  // Header title — generic placeholder until the driver lands.
  const headerTitle =
    state.kind === 'loaded'
      ? state.driver.full_name ?? state.driver.email
      : 'Driver';
  useSetPageHeader({
    title: headerTitle,
    parentLabel: 'Drivers',
    parentTo: '/drivers',
  });

  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    void (async () => {
      // RLS gates the SELECT; cross-tenant driverId returns no row.
      const driverQuery = client
        .from('users')
        .select(
          'id, full_name, email, employee_id, user_type, tenant_role, tenant_id, created_at, drivers:drivers!drivers_id_fkey(status, phone, license_number, license_state, license_expires_on, vehicle_make, vehicle_model, vehicle_color, vehicle_plate, vehicle_state, hire_date, notes)',
        )
        .eq('id', driverId)
        .eq('user_type', 'driver')
        .is('deleted_at', null)
        .maybeSingle();

      const accessQuery = client
        .from('pharmacy_access')
        .select(
          'id, pharmacy_id, granted_at, pharmacies:pharmacies!pharmacy_access_pharmacy_id_fkey(id, name, city), granter:users!pharmacy_access_granted_by_fkey(full_name, email)',
        )
        .eq('user_id', driverId)
        .order('granted_at', { ascending: false });

      const [driverResult, accessResult] = await Promise.all([
        driverQuery,
        accessQuery,
      ]);
      if (cancelled) return;

      if (driverResult.error) {
        captureSupabaseError(driverResult.error, 'drivers.detail.fetch_driver');
        setState({ kind: 'error', message: 'Could not load this driver.' });
        return;
      }
      if (!driverResult.data || !driverResult.data.drivers) {
        // Defensive: user_type='driver' should always have a drivers row,
        // but if for some reason it doesn't, surface as not-found rather
        // than rendering a broken page.
        setState({ kind: 'not_found' });
        return;
      }

      const pharmacyAccess = accessResult.error
        ? []
        : ((accessResult.data ?? []) as unknown as PharmacyAccessRow[]);
      if (accessResult.error) {
        captureSupabaseError(
          accessResult.error,
          'drivers.detail.fetch_pharmacy_access',
        );
      }

      setState({
        kind: 'loaded',
        driver: driverResult.data as unknown as DriverDetailRow,
        pharmacyAccess,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [client, isReady, session, driverId, reloadToken]);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4">
        <Link
          to="/drivers"
          search={{ status: 'active' }}
          className="text-sm text-primary hover:underline"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← Back to drivers
        </Link>
      </div>

      {state.kind === 'loading' ? (
        <Card>
          <p className="text-sm text-ink-2">Loading driver…</p>
        </Card>
      ) : null}

      {state.kind === 'not_found' ? <DriverNotFoundCard onBack={onBack} /> : null}

      {state.kind === 'error' ? (
        <Alert tone="error" title="Something went wrong">
          {state.message}
        </Alert>
      ) : null}

      {state.kind === 'loaded' && state.driver.drivers ? (
        <LoadedView
          driver={state.driver}
          drv={state.driver.drivers}
          pharmacyAccess={state.pharmacyAccess}
          client={client}
          onRefresh={refresh}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoadedView — orchestrates the six panels
// ---------------------------------------------------------------------------

function LoadedView({
  driver,
  drv,
  pharmacyAccess,
  client,
  onRefresh,
}: {
  driver: DriverDetailRow;
  drv: NonNullable<DriverDetailRow['drivers']>;
  pharmacyAccess: PharmacyAccessRow[];
  client: LynrxSupabaseClient;
  onRefresh: () => void;
}): JSX.Element {
  // Status is lifted into local state so lifecycle transitions can
  // update the badge / button label optimistically without a refetch.
  const [currentStatus, setCurrentStatus] = useState<string>(drv.status);
  useEffect(() => setCurrentStatus(drv.status), [drv.status]);

  const [editOpen, setEditOpen] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);

  return (
    <>
      <DriverHeader
        driver={driver}
        currentStatus={currentStatus}
        onStatusChange={setCurrentStatus}
        onEditClick={() => setEditOpen(true)}
        onRefresh={onRefresh}
        client={client}
      />

      <div className="mt-6 grid gap-6">
        <IdentityPanel driver={driver} drv={drv} />

        <LicenseVehiclePanel
          drv={drv}
          onEditClick={() => setEditOpen(true)}
        />

        <PharmacyAccessPanel
          driverId={driver.id}
          tenantId={driver.tenant_id}
          access={pharmacyAccess}
          onGrantClick={() => setGrantOpen(true)}
          onRefresh={onRefresh}
          client={client}
        />

        <RoutesHistoryPanel driverId={driver.id} client={client} />

        <AuditTrailPanel driverId={driver.id} client={client} />
      </div>

      <EditDriverModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        driverId={driver.id}
        drv={drv}
        client={client}
        onSaved={() => {
          setEditOpen(false);
          onRefresh();
        }}
      />

      <GrantPharmacyModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        driverId={driver.id}
        tenantId={driver.tenant_id}
        currentAccess={pharmacyAccess}
        client={client}
        onGranted={() => {
          setGrantOpen(false);
          onRefresh();
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Panel: Header (breadcrumb / status badge / Edit + lifecycle buttons)
// ---------------------------------------------------------------------------

// Lifecycle button choice per current status. The "primary" lifecycle
// action is whichever transition matches operational intent — for
// active, that's "Deactivate" (terminated). For anything else, that's
// "Reactivate" (back to active).
const PRIMARY_LIFECYCLE: Record<
  DriverStatusValue,
  { next: DriverStatusValue; label: string; tone: 'destructive' | 'primary' }
> = {
  active:     { next: 'terminated', label: 'Deactivate',  tone: 'destructive' },
  on_leave:   { next: 'active',     label: 'Reactivate',  tone: 'primary' },
  suspended:  { next: 'active',     label: 'Reactivate',  tone: 'primary' },
  terminated: { next: 'active',     label: 'Reactivate',  tone: 'primary' },
};

function DriverHeader({
  driver,
  currentStatus,
  onStatusChange,
  onEditClick,
  onRefresh,
  client,
}: {
  driver: DriverDetailRow;
  currentStatus: string;
  onStatusChange: (next: string) => void;
  onEditClick: () => void;
  onRefresh: () => void;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    { tone: 'success' | 'error'; text: string } | null
  >(null);

  // Auto-clear feedback after 3s — same pattern as the route detail
  // page driver dropdown.
  useEffect(() => {
    if (!feedback) return;
    const handle = window.setTimeout(() => setFeedback(null), 3000);
    return () => window.clearTimeout(handle);
  }, [feedback]);

  const isKnownStatus = (DRIVER_STATUS_VALUES as readonly string[]).includes(
    currentStatus,
  );
  const lifecycle = isKnownStatus
    ? PRIMARY_LIFECYCLE[currentStatus as DriverStatusValue]
    : null;

  const runTransition = useCallback(
    async (next: DriverStatusValue) => {
      // Destructive transitions need explicit confirmation. Reactivation
      // is permissive enough to one-click.
      if (next === 'terminated' || next === 'suspended') {
        const ok = window.confirm(
          next === 'terminated'
            ? 'Deactivate this driver? All pharmacy access will be revoked. This can be undone by reactivating, but pharmacy access will NOT be auto-restored.'
            : 'Suspend this driver? Existing pharmacy access is preserved.',
        );
        if (!ok) return;
      }
      const previous = currentStatus;
      onStatusChange(next);
      setSubmitting(true);
      setFeedback(null);
      try {
        const { error } = await client.rpc('set_driver_status', {
          p_driver_id:  driver.id,
          p_new_status: next,
        });
        if (error) {
          onStatusChange(previous);
          captureSupabaseError(error, 'drivers.detail.set_driver_status');
          setFeedback({ tone: 'error', text: friendlyDriverError(error.message) });
          return;
        }
        setFeedback({
          tone: 'success',
          text:
            next === 'terminated'
              ? 'Driver deactivated. Pharmacy access revoked.'
              : `Driver status set to ${statusLabel(next)}.`,
        });
        // Status change can cascade-revoke pharmacy_access, so we
        // refresh to pick up the new (empty) list.
        if (next === 'terminated') onRefresh();
      } catch (err) {
        onStatusChange(previous);
        captureSupabaseError(err, 'drivers.detail.set_driver_status.exception');
        setFeedback({ tone: 'error', text: friendlyDriverError(undefined) });
      } finally {
        setSubmitting(false);
      }
    },
    [client, currentStatus, driver.id, onRefresh, onStatusChange],
  );

  return (
    <header className="rounded-md border border-border bg-bg px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-base font-medium text-ink">
            {driver.full_name ?? driver.email}
          </h1>
          <StatusBadge entity="route" status={currentStatus} />
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onEditClick}>
              Edit
            </Button>
            {lifecycle ? (
              <LifecycleButton
                tone={lifecycle.tone}
                disabled={submitting}
                onClick={() => void runTransition(lifecycle.next)}
              >
                {submitting ? 'Working…' : lifecycle.label}
              </LifecycleButton>
            ) : null}
            {/* Secondary lifecycle: from active we offer both Mark on leave
                and Suspend in addition to the primary Deactivate.
                Reactivation is the primary action from non-active states; no
                second control is needed there. */}
            {currentStatus === 'active' ? (
              <>
                <Button
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => void runTransition('on_leave')}
                >
                  Mark on leave
                </Button>
                <Button
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => void runTransition('suspended')}
                >
                  Suspend
                </Button>
              </>
            ) : null}
          </div>
          {feedback ? (
            <p
              role="status"
              className={
                feedback.tone === 'success'
                  ? 'text-xs text-emerald-700'
                  : 'text-xs text-red-700'
              }
            >
              {feedback.text}
            </p>
          ) : null}
        </div>
      </div>
    </header>
  );
}

// Lifecycle button styling — "destructive" tone is red-on-white to
// distinguish Deactivate from the brand-teal primary; "primary" tone
// uses the existing Button primary (teal) for Reactivate.
function LifecycleButton({
  tone,
  disabled,
  onClick,
  children,
}: {
  tone: 'destructive' | 'primary';
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  if (tone === 'primary') {
    return (
      <Button variant="primary" disabled={disabled} onClick={onClick}>
        {children}
      </Button>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Panel: Identity (read-only)
// ---------------------------------------------------------------------------

function IdentityPanel({
  driver,
  drv,
}: {
  driver: DriverDetailRow;
  drv: NonNullable<DriverDetailRow['drivers']>;
}): JSX.Element {
  const tenure = relativeTenure(drv.hire_date);
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Identity</h2>
      <Card>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <DlItem label="Full name" value={driver.full_name ?? '—'} />
          <DlItem label="Email" value={driver.email} />
          <DlItem label="Employee ID" value={driver.employee_id ?? '—'} />
          <DlItem label="Phone" value={drv.phone ?? '—'} />
          <DlItem
            label="Hire date"
            value={
              drv.hire_date
                ? tenure
                  ? `${formatDate(drv.hire_date)} · ${tenure}`
                  : formatDate(drv.hire_date)
                : '—'
            }
          />
          <DlItem
            label="User type"
            value={statusLabel(driver.user_type ?? '')}
          />
          <DlItem
            label="Tenant role"
            value={driver.tenant_role ? statusLabel(driver.tenant_role) : '—'}
          />
        </dl>
      </Card>
    </section>
  );
}

function DlItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: License + Vehicle
// ---------------------------------------------------------------------------

function LicenseVehiclePanel({
  drv,
  onEditClick,
}: {
  drv: NonNullable<DriverDetailRow['drivers']>;
  onEditClick: () => void;
}): JSX.Element {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium text-ink">License &amp; vehicle</h2>
        <Button variant="ghost" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <Card>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <DlItem label="License number" value={drv.license_number ?? '—'} />
          <DlItem label="License state" value={drv.license_state ?? '—'} />
          <DlItem
            label="License expires"
            value={formatDate(drv.license_expires_on)}
          />
          <DlItem label="Vehicle make" value={drv.vehicle_make ?? '—'} />
          <DlItem label="Vehicle model" value={drv.vehicle_model ?? '—'} />
          <DlItem label="Vehicle color" value={drv.vehicle_color ?? '—'} />
          <DlItem label="Vehicle plate" value={drv.vehicle_plate ?? '—'} />
          <DlItem label="Vehicle state" value={drv.vehicle_state ?? '—'} />
          <DlItem label="Notes" value={drv.notes ?? '—'} />
        </dl>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Panel: Pharmacy access
// ---------------------------------------------------------------------------

function PharmacyAccessPanel({
  driverId: _driverId,
  tenantId: _tenantId,
  access,
  onGrantClick,
  onRefresh,
  client,
}: {
  driverId: string;
  tenantId: string | null;
  access: PharmacyAccessRow[];
  onGrantClick: () => void;
  onRefresh: () => void;
  client: LynrxSupabaseClient;
}): JSX.Element {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium text-ink">Pharmacy access</h2>
        <Button variant="secondary" onClick={onGrantClick}>
          Grant access
        </Button>
      </div>
      {access.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-2">
            No pharmacy assignments. Grant access to allow this driver to be
            assigned routes for a specific pharmacy.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {access.map((row) => (
            <PharmacyAccessRowItem
              key={row.id}
              row={row}
              client={client}
              onRevoked={onRefresh}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PharmacyAccessRowItem({
  row,
  client,
  onRevoked,
}: {
  row: PharmacyAccessRow;
  client: LynrxSupabaseClient;
  onRevoked: () => void;
}): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onRevoke = useCallback(async () => {
    if (
      !window.confirm(
        `Revoke ${row.pharmacies?.name ?? 'this pharmacy'} access? Driver will lose the ability to be assigned routes for this pharmacy.`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { error: rpcError } = await client.rpc('revoke_pharmacy_access', {
        p_access_id: row.id,
      });
      if (rpcError) {
        captureSupabaseError(rpcError, 'drivers.detail.revoke_pharmacy_access');
        setError(friendlyDriverError(rpcError.message));
        return;
      }
      onRevoked();
    } catch (err) {
      captureSupabaseError(
        err,
        'drivers.detail.revoke_pharmacy_access.exception',
      );
      setError(friendlyDriverError(undefined));
    } finally {
      setSubmitting(false);
    }
  }, [client, onRevoked, row.id, row.pharmacies?.name]);

  return (
    <li className="rounded-md border border-border bg-bg px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">
            {row.pharmacies?.name ?? row.pharmacy_id}
            {row.pharmacies?.city ? (
              <span className="text-ink-2"> · {row.pharmacies.city}</span>
            ) : null}
          </p>
          <p className="text-xs text-ink-2">
            Granted {formatDateTime(row.granted_at)}
            {row.granter?.full_name || row.granter?.email
              ? ` · by ${row.granter.full_name ?? row.granter.email}`
              : null}
          </p>
        </div>
        <Button variant="ghost" disabled={submitting} onClick={onRevoke}>
          {submitting ? 'Revoking…' : 'Revoke'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Panel: Routes history (filtered, paginated)
// ---------------------------------------------------------------------------

const ROUTES_PAGE_SIZE = 20;

const ROUTE_STATUS_FILTER_VALUES = [
  'all',
  'draft',
  'published',
  'assigned',
  'in_progress',
  'completed',
  'cancelled',
] as const;
type RouteStatusFilter = (typeof ROUTE_STATUS_FILTER_VALUES)[number];
function isRouteStatusFilter(v: unknown): v is RouteStatusFilter {
  return (
    typeof v === 'string' &&
    (ROUTE_STATUS_FILTER_VALUES as readonly string[]).includes(v)
  );
}

function RoutesHistoryPanel({
  driverId,
  client,
}: {
  driverId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [from, setFrom] = useState<string>(daysAgoIso(30));
  const [to, setTo] = useState<string>(todayIso());
  const [status, setStatus] = useState<RouteStatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<RouteHistoryRow[] | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Reset page when filters change so we don't show an empty page-2.
  const fromRef = useRef(from);
  const toRef = useRef(to);
  const statusRef = useRef(status);
  useEffect(() => {
    if (
      fromRef.current !== from ||
      toRef.current !== to ||
      statusRef.current !== status
    ) {
      fromRef.current = from;
      toRef.current = to;
      statusRef.current = status;
      setPage(0);
    }
  }, [from, to, status]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let query = client
        .from('routes')
        .select('id, planned_for, route_status, stops:stops!stops_route_id_fkey(count)')
        .eq('driver_id', driverId)
        .is('deleted_at', null)
        .gte('planned_for', from)
        .lte('planned_for', to)
        .order('planned_for', { ascending: false })
        // Fetch one extra row to detect "has more" without a count query.
        .range(page * ROUTES_PAGE_SIZE, page * ROUTES_PAGE_SIZE + ROUTES_PAGE_SIZE);
      if (status !== 'all') {
        query = query.eq('route_status', status);
      }
      const { data, error } = await query;
      if (cancelled) return;
      if (error) {
        captureSupabaseError(error, 'drivers.detail.fetch_routes');
        setRows([]);
        setHasMore(false);
        return;
      }
      const all = (data ?? []) as unknown as RouteHistoryRow[];
      setHasMore(all.length > ROUTES_PAGE_SIZE);
      setRows(all.slice(0, ROUTES_PAGE_SIZE));
    })();
    return () => {
      cancelled = true;
    };
  }, [client, driverId, from, to, status, page]);

  const columns: Column<RouteHistoryRow>[] = useMemo(
    () => [
      {
        key: 'planned_for',
        header: 'Date',
        render: (r) => (
          <Link
            to="/routes/$routeId"
            params={{ routeId: r.id }}
            className="text-primary hover:underline"
          >
            {formatDate(r.planned_for)}
          </Link>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <StatusBadge entity="route" status={r.route_status} />,
      },
      {
        key: 'stops',
        header: 'Stops',
        render: (r) => String(r.stops[0]?.count ?? 0),
      },
      {
        key: 'id',
        header: 'Route ID',
        render: (r) => <span className="font-mono text-xs">{r.id.slice(0, 8)}</span>,
      },
    ],
    [],
  );

  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Routes history</h2>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-ink-2">
          From
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-border bg-bg px-2 py-1 text-sm text-ink"
            aria-label="Routes history from date"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-2">
          To
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-border bg-bg px-2 py-1 text-sm text-ink"
            aria-label="Routes history to date"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-2">
          Status
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              if (isRouteStatusFilter(next)) setStatus(next);
            }}
            className="rounded-md border border-border bg-bg px-2 py-1 text-sm text-ink"
            aria-label="Routes history status filter"
          >
            {ROUTE_STATUS_FILTER_VALUES.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All' : statusLabel(s)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <DataTable
        columns={columns}
        rows={rows ?? []}
        rowKey={(r) => r.id}
        emptyMessage={
          rows === null ? 'Loading routes…' : 'No routes in this range.'
        }
      />
      <div className="mt-2 flex items-center justify-end gap-2 text-sm">
        <Button
          variant="ghost"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Newer
        </Button>
        <span className="text-xs text-ink-2">Page {page + 1}</span>
        <Button
          variant="ghost"
          disabled={!hasMore}
          onClick={() => setPage((p) => p + 1)}
        >
          Older →
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Panel: Audit trail
// ---------------------------------------------------------------------------

const AUDIT_PAGE_SIZE = 20;

const DRIVER_EVENT_LABELS: Record<string, string> = {
  'driver.created':         'Driver created',
  'driver.updated':         'Driver profile updated',
  'driver.reactivated':     'Driver reactivated',
  'driver.on_leave':        'Driver marked on leave',
  'driver.suspended':       'Driver suspended',
  'driver.terminated':      'Driver deactivated',
  'pharmacy_access.granted': 'Pharmacy access granted',
  'pharmacy_access.revoked': 'Pharmacy access revoked',
  'route.driver_assigned':   'Assigned to route',
  'route.driver_unassigned': 'Unassigned from route',
};

function AuditTrailPanel({
  driverId,
  client,
}: {
  driverId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<AuditEventRow[] | null>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Per upstream spec: actor_user_id = driver_id OR
      // (entity_type='driver' AND entity_id=driver_id). PostgREST
      // .or() supports nested logical operators via and(...).
      const { data, error } = await client
        .from('audit_events')
        .select(
          'id, occurred_at, event_type, actor_user_id, actor_type, payload, actor_user:users!audit_events_actor_user_id_fkey(full_name, email)',
        )
        .or(
          `actor_user_id.eq.${driverId},and(entity_type.eq.driver,entity_id.eq.${driverId})`,
        )
        .order('occurred_at', { ascending: false })
        .range(page * AUDIT_PAGE_SIZE, page * AUDIT_PAGE_SIZE + AUDIT_PAGE_SIZE);
      if (cancelled) return;
      if (error) {
        captureSupabaseError(error, 'drivers.detail.fetch_audit');
        setRows([]);
        setHasMore(false);
        return;
      }
      const all = (data ?? []) as unknown as AuditEventRow[];
      setHasMore(all.length > AUDIT_PAGE_SIZE);
      setRows(all.slice(0, AUDIT_PAGE_SIZE));
    })();
    return () => {
      cancelled = true;
    };
  }, [client, driverId, page]);

  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Activity</h2>
      {rows === null ? (
        <p className="text-sm text-ink-2">Loading activity…</p>
      ) : (
        <>
          <AuditEventList
            rows={rows}
            emptyMessage="No activity yet."
            eventLabel={(t) => DRIVER_EVENT_LABELS[t] ?? t}
          />
          <div className="mt-2 flex items-center justify-end gap-2 text-sm">
            <Button
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Newer
            </Button>
            <span className="text-xs text-ink-2">Page {page + 1}</span>
            <Button
              variant="ghost"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Older →
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Modal: Edit driver
// ---------------------------------------------------------------------------

type EditFormState = {
  phone: string;
  license_number: string;
  license_state: string;
  license_expires_on: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_color: string;
  vehicle_plate: string;
  vehicle_state: string;
  hire_date: string;
  notes: string;
};

function buildEditDiff(
  initial: EditFormState,
  next: EditFormState,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  (Object.keys(next) as (keyof EditFormState)[]).forEach((key) => {
    if (initial[key] !== next[key]) {
      // Empty string → null so the RPC clears the column.
      out[key] = next[key].trim() === '' ? null : next[key].trim();
    }
  });
  return out;
}

function initialEditState(
  drv: NonNullable<DriverDetailRow['drivers']>,
): EditFormState {
  return {
    phone:              drv.phone              ?? '',
    license_number:     drv.license_number     ?? '',
    license_state:      drv.license_state      ?? '',
    license_expires_on: drv.license_expires_on ?? '',
    vehicle_make:       drv.vehicle_make       ?? '',
    vehicle_model:      drv.vehicle_model      ?? '',
    vehicle_color:      drv.vehicle_color      ?? '',
    vehicle_plate:      drv.vehicle_plate      ?? '',
    vehicle_state:      drv.vehicle_state      ?? '',
    hire_date:          drv.hire_date          ?? '',
    notes:              drv.notes              ?? '',
  };
}

function EditDriverModal({
  open,
  onClose,
  driverId,
  drv,
  client,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  driverId: string;
  drv: NonNullable<DriverDetailRow['drivers']>;
  client: LynrxSupabaseClient;
  onSaved: () => void;
}): JSX.Element {
  const [initial, setInitial] = useState<EditFormState>(() =>
    initialEditState(drv),
  );
  const [form, setForm] = useState<EditFormState>(() => initialEditState(drv));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state when the modal opens with possibly-changed driver data.
  useEffect(() => {
    if (open) {
      const fresh = initialEditState(drv);
      setInitial(fresh);
      setForm(fresh);
      setSubmitError(null);
    }
  }, [open, drv]);

  const set = useCallback(
    <K extends keyof EditFormState>(key: K, value: string) =>
      setForm((f) => ({ ...f, [key]: value })),
    [],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const diff = buildEditDiff(initial, form);
      if (Object.keys(diff).length === 0) {
        setSubmitError('No changes to save.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const { error } = await client.rpc('update_driver_profile', {
          p_driver_id: driverId,
          p_updates:   diff,
        });
        if (error) {
          captureSupabaseError(error, 'drivers.detail.update_profile');
          setSubmitError(friendlyDriverError(error.message));
          return;
        }
        onSaved();
      } catch (err) {
        captureSupabaseError(err, 'drivers.detail.update_profile.exception');
        setSubmitError(friendlyDriverError(undefined));
      } finally {
        setSubmitting(false);
      }
    },
    [client, driverId, form, initial, onSaved],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit driver"
      titleId="edit-driver-title"
      widthClass="max-w-2xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-driver-form"
            variant="primary"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form
        id="edit-driver-form"
        onSubmit={onSubmit}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <Field
          label="Phone"
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
        />
        <Field
          label="Hire date"
          type="date"
          value={form.hire_date}
          onChange={(e) => set('hire_date', e.target.value)}
        />
        <Field
          label="License number"
          value={form.license_number}
          onChange={(e) => set('license_number', e.target.value)}
        />
        <Field
          label="License state"
          value={form.license_state}
          onChange={(e) => set('license_state', e.target.value)}
        />
        <Field
          label="License expires"
          type="date"
          value={form.license_expires_on}
          onChange={(e) => set('license_expires_on', e.target.value)}
        />
        <Field
          label="Vehicle make"
          value={form.vehicle_make}
          onChange={(e) => set('vehicle_make', e.target.value)}
        />
        <Field
          label="Vehicle model"
          value={form.vehicle_model}
          onChange={(e) => set('vehicle_model', e.target.value)}
        />
        <Field
          label="Vehicle color"
          value={form.vehicle_color}
          onChange={(e) => set('vehicle_color', e.target.value)}
        />
        <Field
          label="Vehicle plate"
          value={form.vehicle_plate}
          onChange={(e) => set('vehicle_plate', e.target.value)}
        />
        <Field
          label="Vehicle state"
          value={form.vehicle_state}
          onChange={(e) => set('vehicle_state', e.target.value)}
        />
        <div className="sm:col-span-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">Notes</span>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </label>
        </div>
        {submitError ? (
          <div className="sm:col-span-2">
            <Alert tone="error">{submitError}</Alert>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: Grant pharmacy access
// ---------------------------------------------------------------------------

function GrantPharmacyModal({
  open,
  onClose,
  driverId,
  tenantId,
  currentAccess,
  client,
  onGranted,
}: {
  open: boolean;
  onClose: () => void;
  driverId: string;
  tenantId: string | null;
  currentAccess: PharmacyAccessRow[];
  client: LynrxSupabaseClient;
  onGranted: () => void;
}): JSX.Element {
  const [pharmacies, setPharmacies] = useState<PharmacyOption[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load pharmacies in the driver's tenant on open. RLS scopes to the
  // caller's tenant automatically, which matches the driver's tenant
  // (drivers are tenant-scoped). For platform_admin the load may
  // return empty per the existing tenants_select RLS gap — surface
  // a hint in that case rather than failing silently.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPharmacies(null);
    setSubmitError(null);
    setSelectedId('');
    void (async () => {
      const { data, error } = await client
        .from('pharmacies')
        .select('id, name, city')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        captureSupabaseError(error, 'drivers.detail.fetch_grant_pharmacies');
        setPharmacies([]);
        return;
      }
      setPharmacies(
        (data ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          city: p.city ?? null,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [client, open, tenantId]);

  // Exclude pharmacies the driver already has access to.
  const grantedIds = useMemo(
    () => new Set(currentAccess.map((r) => r.pharmacy_id)),
    [currentAccess],
  );
  const availablePharmacies = useMemo(
    () => (pharmacies ?? []).filter((p) => !grantedIds.has(p.id)),
    [pharmacies, grantedIds],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedId) {
        setSubmitError('Pick a pharmacy.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const { error } = await client.rpc('grant_pharmacy_access', {
          p_driver_id:   driverId,
          p_pharmacy_id: selectedId,
        });
        if (error) {
          captureSupabaseError(error, 'drivers.detail.grant_pharmacy_access');
          setSubmitError(friendlyDriverError(error.message));
          return;
        }
        onGranted();
      } catch (err) {
        captureSupabaseError(
          err,
          'drivers.detail.grant_pharmacy_access.exception',
        );
        setSubmitError(friendlyDriverError(undefined));
      } finally {
        setSubmitting(false);
      }
    },
    [client, driverId, onGranted, selectedId],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Grant pharmacy access"
      titleId="grant-pharmacy-title"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="grant-pharmacy-form"
            variant="primary"
            disabled={submitting || !selectedId}
          >
            {submitting ? 'Granting…' : 'Grant access'}
          </Button>
        </>
      }
    >
      <form id="grant-pharmacy-form" onSubmit={onSubmit} className="space-y-3">
        {pharmacies === null ? (
          <p className="text-sm text-ink-2">Loading pharmacies…</p>
        ) : availablePharmacies.length === 0 ? (
          <Alert tone="info">
            {grantedIds.size > 0
              ? 'Driver already has access to every pharmacy in your scope.'
              : 'No pharmacies visible. If you are a platform admin, the tenant-scoped pharmacies RLS may not match your token.'}
          </Alert>
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink">Pharmacy</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Pharmacy to grant access to"
            >
              <option value="">— Select pharmacy —</option>
              {availablePharmacies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.city ? ` · ${p.city}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Not-found surfaces
// ---------------------------------------------------------------------------

export function DriverNotFoundCard({
  onBack,
}: {
  onBack: () => void;
}): JSX.Element {
  return (
    <Alert tone="error" title="Driver not found">
      We couldn&apos;t find a driver with that ID.
      <div className="mt-3">
        <Button variant="secondary" onClick={onBack}>
          Back to drivers
        </Button>
      </div>
    </Alert>
  );
}

function DriverNotFoundRoute(): JSX.Element {
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/drivers', search: { status: 'active' } });
  }, [navigate]);
  return (
    <main className="min-h-screen bg-bg-soft px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <Link
            to="/drivers"
            search={{ status: 'active' }}
            className="text-sm text-primary hover:underline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back to drivers
          </Link>
        </div>
        <DriverNotFoundCard onBack={onBack} />
      </div>
    </main>
  );
}
